import React, { Suspense, useState, useEffect } from 'react';
import {ApolloProvider, useLazyQuery} from '@apollo/react-hooks';
import './App.scss';
import { Subject } from 'rxjs';
import GraphQlClient from './services/graphql';
import { Navigate, HashRouter, Route, Routes } from 'react-router-dom';
import LoginComponent from './auth/Login';
import ProgramsBase from './programs/Programs';
import ProgramCreate from './programs/create/Program.create';
import ProgramDetail from './programs/detail/Program.detail';
import ProgramUsers from './programs/users/Program.users';
import ProgramTeams from './programs/teams/Program.teams';
import AppMenu from './menu/menu';
import DevicesBase from './devices/Devices';
import PatientsBase from './patients/Patients';
import PatientAssign from './patients/assignment/patient.assign';
import DevicesSubtype from './devices/Devices.subtype';
import DevicesKits from './devices/Devices.kits';
import RecoverComponent from './auth/Recover.password';
import MenuSlider from './menu/menuSlider';
import ResetPasswordComponent from './auth/Reset.password';
import UnlockAccountComponent from "./auth/Unlock.Account";
import UsersBase from './users/Users';
import UserCreate from './users/assignment/Users.create';
import UsersDetail from './users/detail/Users.detail';
import PatientDetail from './patients/detail/Patients.detail';
import ActivateAccountComponent from './auth/Activate.account';
import ModalExpired from './visuals/ModalExpired';
import sessionController from './services/sessionController';
import historyState from "./services/history.state";
import VitalCreate from "./programs/vital/Vital.create";
import Teams from "./teams/Teams";
import TeamDetail from "./teams/detail/Team.detail";
import TeamCreate from "./teams/create/Team.create";
import TeamManage from "./teams/manage/Team.manage";
//Import for translations
import { useTranslation } from 'react-i18next';
import gqlSchemaLibrary from "./services/graphql.schemas";
import LocalStorageUtil from "./services/localStorageUtil";
import AuditTrail, {PAGES} from "./services/interactions/audit.trail";

const loading = (
  <>
    <span>Loading...</span>
  </>
)

function App(props) {
  // SHARED VARIABLES
  const toasterSubject = new Subject();

  const [menuOpen, _openMenu] = useState(false);

  const [userFetched, setUserFetched] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [loadUser, setLoadUser] = useState(null);
  const [currentOrg, setCurrentOrg] = useState(null);

  const [redirect, setRedirect] = useState(null);
  //Setup translations
  const { t} = useTranslation();
  const [expired, setExpired] = useState(false);
  const [redirectExpired, setRedirectExpired] = useState(false);

  const setUser = (user, _link) => {
    setCurrentUser((actualUser) => {
          console.log('[APPJS] getCurrentUser: ', actualUser, user);
          if (!_link && !user && sessionController.isExpired) {
            setExpired(true);
            sessionController.removeExpiredSession();
          }
          setRedirect(user && _link? _link : null);
          return user;
        });
    AuditTrail.setUser(user);
    setUserFetched(true);
    if (!!user && !!user.clinician && user.clinician.role === 'command-only') {
      setTimeout(() => {
        window.location.replace(process.env.REACT_APP_COMMAND_URI);
      }, 2500);
    }
  }

  const [getRedirectHash] = useLazyQuery(gqlSchemaLibrary.user.query.getURIFromHash, {
      client: GraphQlClient.client,
      onCompleted: (data) => {
        const _link = data.getURIFromHash;
        if (_link) {
          const _params = _link.split('@');
          let link = _link;
          if (_params.length !== 0) {
            link = _params[0];
            if (_params.length > 1) {
              LocalStorageUtil.setItem('params', JSON.stringify(_params));
            }
          }
          if (link) {
            setUser(loadUser, link);
          } else {
            setRedirectExpired(true);
            setUserFetched(true);
          }
        } else {
          setRedirectExpired(true);
          setUserFetched(true);
        }
      },
      onError: (err) => {
        LocalStorageUtil.removeItem('params');
        setUser(loadUser);
      },
      fetchPolicy: 'cache-and-network'
  });

  const onRedirect = (user, hash) => {
      setLoadUser(user);
      getRedirectHash({
        variables: {
          hash: hash
        }
      });
  }

  useEffect(() => {
    setRedirect(null);
    GraphQlClient.getCurrentUser()
      .subscribe((user) => {
        const hash = historyState.getStateFromSearch.redirect;
        if (user && hash) {
          onRedirect(user, hash);
        } else {
          setUser(user);
        }
      }, (e) => {
        console.log('Error loging user: ', e);
        setUserFetched(true);
      });
    GraphQlClient.getCurrentOrg().subscribe((org) => {
        setCurrentOrg(org);
        AuditTrail.setOrganization(org);
    });
  }, [])

  const renderForRedirectOnLogged = (RenderComponent) => {
    if (redirect) {
      if (window.location.pathname !== redirect) {
        return <Navigate to={redirect}/>;
      }
    }
    return !!currentUser ? <Navigate to="/programs" /> : RenderComponent;
  }

  const renderForRedirect = (RenderComponent) => {
    return !currentUser ? <Navigate to="/login" /> : RenderComponent;
  }

  const renderMenuForUsers = (AppMenuComponent) => {
    return currentUser ? AppMenuComponent : '';
  }

  const toggleMenu = () => {
    AuditTrail.sendLog(PAGES.PAGE_VIEW, {tag: 'button', event: 'onClick', action: 'header button menu', open: !menuOpen});
    _openMenu(!menuOpen);
  }

  const onLogout = () => {
    _openMenu(false);
  }

  const masterClass = menuOpen && currentUser ? "Master displayMenu" : "Master";

  const renderUnauthorized = () => {
    return (
      <div className='App'>
        <h1 style={{ margin: '5em 0em', color: 'white', 'text-align': 'center'}}></h1>
      </div>
    )
  }

  const renderExpiration = () => {
    return (
      <div className='App Expired'>
        <div className="container">
          <div className="_title">
            <h1>Invalid token</h1>
          </div>
          <p>
            This token is no longer valid
          </p>
        </div>
      </div>
    )
  }

  const onCloseModalExpired = () => {
    setExpired(false);
  }

  const renderMain = () => {
    if (redirectExpired) {
      return renderExpiration();
    }
    if (!!currentUser && !!currentUser.clinician && currentUser.clinician.role === 'command-only') {
      return renderUnauthorized()
    }
    return (
      <HashRouter>
        <div className='App'>
          {renderMenuForUsers(<AppMenu currentUser={currentUser} toggleMenu={toggleMenu} onLogout={onLogout}/>)}
          <div className={masterClass}>
            {renderMenuForUsers(<MenuSlider currentUser={currentUser} onLogout={onLogout}/>)}
            {
              menuOpen && currentUser?
              <div className="___sliderOverlay" onClick={toggleMenu}/>
              : ''
            }
            {
                <ModalExpired currentUser={currentUser} isOpen={expired} onClose={onCloseModalExpired}/>
            }
            <Suspense fallback={loading}>
            <Routes>
              <Route path="*" element={renderForRedirect(
                <Navigate to="/programs" {...props} />
              )} />
              <Route exact path="/password" element={renderForRedirectOnLogged(
                <RecoverComponent {...props}
                  setUser={setCurrentUser}
                  eventParams={{page: PAGES.RECOVER_PASSWORD}}
                />
              )} />
              <Route exact path="/resetPassword/:token" element={renderForRedirectOnLogged(
                <ResetPasswordComponent {...props}
                  setUser={setCurrentUser}
                  eventParams={{page: PAGES.RESET_PASSWORD}}
                />
              )} />
              <Route exact path="/activateAccount/:token" element={renderForRedirectOnLogged(
                <ActivateAccountComponent {...props}
                  setUser={setCurrentUser}
                  eventParams={{page: PAGES.ACTIVATE_ACCOUNT}}
                />
              )} />
              <Route exact path="/unlockAccount/:token" element={renderForRedirectOnLogged(
                  <UnlockAccountComponent {...props}
                    setUser={setCurrentUser}
                    eventParams={{page: PAGES.UNLOCK_ACCOUNT}}
                  />
              )} />
              <Route exact path="/login" element={renderForRedirectOnLogged(
                <LoginComponent {...props}
                  setUser={setCurrentUser}
                  toaster={toasterSubject}
                  setRedirectExpired={setRedirectExpired}
                  eventParams={{page: PAGES.LOGIN}}
                />
              )} />
              <Route exact path="/users" element={renderForRedirect(
                <UsersBase {...props}
                  toaster={toasterSubject}
                  currentOrg={currentOrg}
                  currentUser={currentUser}
                  eventParams={{page: PAGES.USERS_LIST}}
                />

              )} />
              <Route exact path="/teams" element={renderForRedirect(
                <Teams {...props}
                  event={PAGES.TEAMS_LIST}
                  toaster={toasterSubject}
                  currentOrg={currentOrg}
                  currentUser={currentUser}
                  eventParams={{page: PAGES.TEAMS_LIST}}
                />

              )} />
              <Route exact path="/teams/detail/:teamId" element={renderForRedirect(
                <TeamDetail {...props}
                  event={PAGES.TEAMS_DETAIL}
                  toaster={toasterSubject}
                  currentUser={currentUser}
                  eventParams={{page: PAGES.TEAMS_DETAIL}}
                />

              )} />
              <Route exact path="/teams/create" element={renderForRedirect(
                <TeamCreate {...props}
                  event={PAGES.TEAMS_CREATE}
                  toaster={toasterSubject}
                  currentOrg={currentOrg}
                  currentUser={currentUser}
                  eventParams={{page: PAGES.TEAMS_CREATE}}
                />

              )} />
              <Route exact path="/teams/edit/:teamId" element={renderForRedirect(
                <TeamCreate {...props}
                  event={PAGES.TEAMS_EDIT}
                  toaster={toasterSubject}
                  currentOrg={currentOrg}
                  currentUser={currentUser}
                  eventParams={{page: PAGES.TEAMS_EDIT}}
                />

              )} />
               <Route exact path="/teams/manage/:teamId" element={renderForRedirect(
                <TeamManage {...props}
                  event={PAGES.TEAMS_MANAGE}
                  toaster={toasterSubject}
                  currentOrg={currentOrg}
                  currentUser={currentUser}
                  eventParams={{page: PAGES.TEAMS_MANAGE}}
                />

              )} />
              <Route exact path="/users/create" element={renderForRedirect(
                <UserCreate {...props}
                  event={PAGES.USERS_CREATE}
                  toaster={toasterSubject}
                  currentUser={currentUser}
                  eventParams={{page: PAGES.USERS_CREATE}}
                />

              )} />
              <Route exact path="/users/edit/:clinicianId" element={renderForRedirect(
                <UserCreate {...props}
                  event={PAGES.USERS_EDIT}
                  toaster={toasterSubject}
                  currentUser={currentUser}
                  eventParams={{page: PAGES.USERS_EDIT}}
                />

              )} />
              <Route exact path="/users/view/:clinicianId" element={renderForRedirect(
                <UsersDetail {...props}
                  currentUser={currentUser}
                  eventParams={{page: PAGES.USERS_DETAIL}}
                />
              )} />
              <Route exact path="/participants" element={renderForRedirect(
                <PatientsBase {...props}
                  toaster={toasterSubject}
                  currentOrg={currentOrg}
                  currentUser={currentUser}
                  eventParams={{page: PAGES.PARTICIPANTS_LIST}}
                />

              )} />
              <Route exact path="/participants/create" element={renderForRedirect(
                <PatientAssign {...props}
                  event={PAGES.PARTICIPANTS_CREATE}
                  toaster={toasterSubject}
                  currentUser={currentUser}
                  eventParams={{page: PAGES.PARTICIPANTS_CREATE}}
                />

              )} />
              <Route exact path="/participants/edit/:patientid" element={renderForRedirect(
                <PatientAssign {...props}
                  event={PAGES.PARTICIPANTS_EDIT}
                  toaster={toasterSubject}
                  currentUser={currentUser}
                  eventParams={{page: PAGES.PARTICIPANTS_EDIT}}
                />
              )} />
              <Route exact path="/participants/view/:patientid" element={renderForRedirect(
                <PatientDetail {...props}
                  toaster={toasterSubject}
                  currentOrg={currentOrg}
                  currentUser={currentUser}
                  eventParams={{page: PAGES.PARTICIPANTS_DETAIL}}
                />
              )} />
              <Route exact path="/programs" element={renderForRedirect(
                <ProgramsBase {...props}
                  toaster={toasterSubject}
                  currentOrg={currentOrg}
                  currentUser={currentUser}
                  eventParams={{page: PAGES.PROGRAM_LIST}}
                />
              )} />
              <Route exact path="/programs/detail/:programId" element={renderForRedirect(
                <ProgramDetail {...props}
                  currentUser={currentUser}
                  eventParams={{page: PAGES.PROGRAM_DETAIL}}
                />
              )} />
              <Route path="/programs/create/:templateID?" element={renderForRedirect(
                <ProgramCreate {...props}
                  toaster={toasterSubject}
                  currentUser={currentUser}
                  eventParams={{page: PAGES.PROGRAM_CREATE}}
                />
              )} />
              <Route path="/programs/edit/:programID?" element={renderForRedirect(
                <ProgramCreate {...props}
                  toaster={toasterSubject}
                  currentUser={currentUser}
                  eventParams={{page: PAGES.PROGRAM_EDIT}}
                />
              )} />
              <Route path="/programs/:programId/create/vital/" element={renderForRedirect(
                <VitalCreate {...props}
                             currentUser={currentUser}
                />
              )} />
              <Route path="/programs/:programId/participants" element={renderForRedirect(
                <ProgramsBase {...props}
                  toaster={toasterSubject}
                  currentOrg={currentOrg}
                  currentUser={currentUser}
                  eventParams={{page: PAGES.PROGRAM_PARTICIPANTS}}
                />
              )} />
              <Route path="/programs/:programId/users" element={renderForRedirect(
                <ProgramUsers {...props}
                  toaster={toasterSubject}
                  currentOrg={currentOrg}
                  currentUser={currentUser}
                  eventParams={{page: PAGES.PROGRAM_USERS}}
                />
              )} />
              <Route path="/programs/:programId/teams" element={renderForRedirect(
                <ProgramTeams {...props}
                  toaster={toasterSubject}
                  currentOrg={currentOrg}
                  currentUser={currentUser}
                  eventParams={{page: PAGES.PROGRAM_TEAMS}}
                />
              )} />
              <Route exact path="/devices" element={renderForRedirect(
                <DevicesBase {...props}
                  toaster={toasterSubject}
                  currentOrg={currentOrg}
                  currentUser={currentUser}
                  eventParams={{page: PAGES.DEVICE_LIST}}
                />
              )} />
              <Route path="/devices/kits/" element={renderForRedirect(
                <DevicesKits {...props}
                  toaster={toasterSubject}
                  currentOrg={currentOrg}
                  currentUser={currentUser}
                  eventParams={{page: PAGES.DEVICE_KIT_LIST}}
                />
              )} />
              <Route path="/devices/:devicesubtype" element={renderForRedirect(
                <DevicesSubtype {...props}
                  toaster={toasterSubject}
                  currentOrg={currentOrg}
                  currentUser={currentUser}
                  eventParams={{page: PAGES.DEVICE_TYPE_LIST}}
                />
              )} />
            </Routes>
            </Suspense>
          </div>
        </div>
      </HashRouter>
    )
  }

  const renderLoading = () => {
    return (
      <div id="_AppLoading">
         {t('loading-user')}
      </div>
    )
  }
  return (
    <ApolloProvider client={GraphQlClient.client}>
      {
        userFetched ?
          renderMain() :
          renderLoading()
      }
    </ApolloProvider>
  )
}

export default App;
